import React, { useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  ResponsiveContainer,
} from "recharts";
import { _API } from "../../../../modules/utils";

const POLL_INTERVAL_MS = 5000;
const MAX_POINTS = 20;

const fmt = (ts) => {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const toBps = (v) => (v ? (v / 1_000_000).toFixed(2) : 0);
const toSec = (ms) => (ms ? (ms / 1000).toFixed(2) : 0);

const CHART_COLORS = {
  inputVideo:  "#4e9eff",
  inputAudio:  "#3b3b9e",
  outputVideo: "#52c41a",
  outputAudio: "#b7eb8f",
  keyframe:    "#4e9eff",
  inputFps:    "#4e9eff",
  outputFps:   "#52c41a",
};

function StatusChart({ title, data, lines, yAxisLabel }) {
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "14px 12px 10px", background: "#fff" }}>
      <p style={{ margin: "0 0 8px", textAlign: "center", fontSize: 13, fontWeight: 600, color: "#1f2937" }}>
        {title}
      </p>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 4, right: 12, left: 20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 10, fill: "#94a3b8" }}
            interval="preserveStartEnd"
          />
          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} width={40}>
            <Label
              value={yAxisLabel}
              angle={-90}
              position="insideLeft"
              offset={-8}
              style={{ fontSize: 10, fill: "#94a3b8" }}
            />
          </YAxis>
          <Tooltip
            contentStyle={{ fontSize: 11, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6 }}
          />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="line" />
          {lines.map((l) => (
            <Line
              key={l.key}
              type="monotone"
              dataKey={l.key}
              name={l.name}
              stroke={l.color}
              strokeWidth={1.5}
              dot={{ r: 3, fill: l.color }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const filterBtnBase = {
  border: "1px solid #e5e7eb",
  borderRadius: 4,
  padding: "3px 8px",
  fontSize: 11,
  cursor: "pointer",
  background: "#fff",
  color: "#475569",
};

const filterBtnActive = {
  ...filterBtnBase,
  borderColor: "#5959ef",
  color: "#5959ef",
  background: "#f5f3ff",
};

export default function ConsoleStreamStatusChart({ channelId, channelName, siteIdx, isActive }) {
  const [points, setPoints] = useState([]);
  const [error, setError] = useState(null);
  const [showBitrate,  setShowBitrate]  = useState(true);
  const [showFps,      setShowFps]      = useState(true);
  const [showKeyframe, setShowKeyframe] = useState(true);
  const timerRef = useRef(null);

  const fetchAndAppend = async () => {
    try {
      const res = await _API.post({
        path: "/v1/media-center/channels/monitoring",
        data: { context: "rail", siteIdx, channelId },
      });
      if (res?.error !== 0) return;

      const master = res.data?.master;
      if (!master?.input && !master?.output) return;

      const ts = res.data?.timestamp || Date.now();
      const { input = {}, output = {} } = master;

      setPoints((prev) => {
        const next = [
          ...prev,
          {
            time: fmt(ts),
            inputVideoBitrate:  Number(toBps(input.videoBitrate)),
            inputAudioBitrate:  Number(toBps(input.audioBitrate)),
            outputVideoBitrate: Number(toBps(output.videoBitrate)),
            outputAudioBitrate: Number(toBps(output.audioBitrate)),
            keyframeInterval:   Number(toSec(output.keyframeDtsIntervalMs ?? input.keyframeDtsIntervalMs)),
            inputFps:  input.videoFramerate  ?? 0,
            outputFps: output.videoFramerate ?? 0,
          },
        ];
        return next.length > MAX_POINTS ? next.slice(next.length - MAX_POINTS) : next;
      });
      setError(null);
    } catch {
      setError("모니터링 데이터를 불러오지 못했습니다.");
    }
  };

  useEffect(() => {
    if (!channelId || !isActive) {
      clearInterval(timerRef.current);
      return;
    }
    fetchAndAppend();
    timerRef.current = setInterval(fetchAndAppend, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [channelId, isActive]);

  return (
    <div>
      {/* 헤더 */}
      <div style={{ marginBottom: 14 }}>
        <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#5959ef" }}>
          {channelName} 라이브가 선택 되었습니다.
        </p>
        <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
          Input stream 과 Output stream 상태를 실시간으로 확인 할 수 있습니다.
        </p>
      </div>

      {/* 필터 */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 11, color: "#64748b", marginRight: 2 }}>필터</span>
        <button style={showBitrate ? filterBtnActive : filterBtnBase} onClick={() => setShowBitrate(v => !v)}>
          Average Bitrate: {showBitrate ? "✓ 전체" : "숨김"}
        </button>
        <button style={showFps ? filterBtnActive : filterBtnBase} onClick={() => setShowFps(v => !v)}>
          Average fps: {showFps ? "✓ 전체" : "숨김"}
        </button>
        <button style={showKeyframe ? filterBtnActive : filterBtnBase} onClick={() => setShowKeyframe(v => !v)}>
          Keyframe interval: {showKeyframe ? "✓ 적용" : "숨김"}
        </button>
      </div>

      {error && (
        <div style={{ padding: "40px 0", textAlign: "center", fontSize: 13, color: "#94a3b8" }}>{error}</div>
      )}

      {!error && points.length === 0 && (
        <div style={{ padding: "40px 0", textAlign: "center", fontSize: 13, color: "#94a3b8" }}>
          데이터를 불러오는 중...
        </div>
      )}

      {!error && points.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {showBitrate && (
            <StatusChart
              title="Average Bitrate"
              data={points}
              yAxisLabel="Mbps"
              lines={[
                { key: "inputVideoBitrate",  name: "Input Video Bitrate",  color: CHART_COLORS.inputVideo },
                { key: "inputAudioBitrate",  name: "Input Audio Bitrate",  color: CHART_COLORS.inputAudio },
                { key: "outputVideoBitrate", name: "Output Video Bitrate", color: CHART_COLORS.outputVideo },
                { key: "outputAudioBitrate", name: "Output Audio Bitrate", color: CHART_COLORS.outputAudio },
              ]}
            />
          )}
          {showKeyframe && (
            <StatusChart
              title="Keyframe Interval"
              data={points}
              yAxisLabel="Keyframe interval(s)"
              lines={[
                { key: "keyframeInterval", name: "Keyframe interval", color: CHART_COLORS.keyframe },
              ]}
            />
          )}
          {showFps && (
            <StatusChart
              title="Average FPS"
              data={points}
              yAxisLabel="Avg fps"
              lines={[
                { key: "inputFps",  name: "Input Fps",  color: CHART_COLORS.inputFps },
                { key: "outputFps", name: "Output Fps", color: CHART_COLORS.outputFps },
              ]}
            />
          )}
        </div>
      )}
    </div>
  );
}
